/**
 * Choosing what the shop uses: how much, and a few yes/no questions. Written
 * for a shop owner, not a developer — no module keys on screen.
 */
const modules = {
  en: {
    title: "What your shop uses",
    intro: "Switch things on or off to match how you work. Turning something off only hides it: nothing is deleted, and it all comes back when you turn it on again.",
    howMuch: "How much do you need?",
    presets: {
      counter: { name: "Just the counter", desc: "Sell, manage your products and keep track of stock." },
      profit: { name: "Counter plus profit", desc: "Adds buy prices, expenses, suppliers, deliveries and what you really earn." },
      mixing: { name: "I mix my own e-liquids", desc: "Adds materials, recipes and batches, on top of profit tracking." },
      full: { name: "Everything", desc: "Everything your plan includes." },
    },
    questions: "And do you…",
    items: {
      credit_sales: { name: "Sell on credit", desc: "Customers keep a tab and pay you back later." },
      preorders: { name: "Take pre-orders from the menu", desc: "Customers reserve products and collect them at the counter." },
      offers: { name: "Run offers and discounts", desc: "Percentage, fixed and buy-X-get-Y offers." },
      packs: { name: "Sell packs", desc: "Bundles of products sold together at one price." },
      cash_drawer: { name: "Count the cash drawer", desc: "Open and close the drawer with a blind count every day." },
      customer_menu: { name: "Have a customer menu", desc: "The page customers reach by scanning your QR code." },
      finance: { name: "Profit tracking", desc: "Buy prices, expenses, deliveries and revenue reports." },
      suppliers: { name: "Suppliers", desc: "Who you buy from, what you owe them and what you paid." },
      mixing: { name: "Mixing", desc: "Materials, recipes and batches for your own e-liquids." },
      savings: { name: "Savings", desc: "The money you keep aside from the shop." },
      reminders: { name: "Reminders", desc: "Notifications at a time you choose, for example your shopping list." },
      journal: { name: "Journal", desc: "Who did what, when, and on which device." },
    },
    advanced: "All the switches",
    needs: "Needs: {names}",
    alsoOff: "Off because {names} is off.",
    notInPlan: "Not part of your plan",
    on: "On",
    off: "Off",
    saving: "Saving…",
    saved: "Saved.",
    saveFailed: "Could not save this change.",
    notOffered: "That is not part of your plan. Ask for a bigger plan to use it.",
    alsoTurnsOn: "This also turns on {names}.",
    alsoTurnsOff: "This also turns off {names}.",
    loadFailed: "Could not load your settings.",
  },
  fr: {
    title: "Ce que votre boutique utilise",
    intro: "Activez ou désactivez selon votre façon de travailler. Désactiver ne fait que masquer : rien n'est supprimé, et tout revient quand vous réactivez.",
    howMuch: "De quoi avez-vous besoin ?",
    presets: {
      counter: { name: "Juste la caisse", desc: "Vendre, gérer vos produits et suivre le stock." },
      profit: { name: "Caisse et bénéfices", desc: "Ajoute les prix d'achat, les dépenses, les fournisseurs, les livraisons et ce que vous gagnez vraiment." },
      mixing: { name: "Je prépare mes e-liquides", desc: "Ajoute les matières, les recettes et les lots, en plus du suivi des bénéfices." },
      full: { name: "Tout", desc: "Tout ce que votre offre comprend." },
    },
    questions: "Et est-ce que vous…",
    items: {
      credit_sales: { name: "Vendez à crédit", desc: "Les clients ont une ardoise et vous remboursent plus tard." },
      preorders: { name: "Prenez des précommandes depuis le menu", desc: "Les clients réservent des produits et viennent les retirer au comptoir." },
      offers: { name: "Faites des offres et des remises", desc: "Pourcentage, montant fixe, offres « X achetés, Y offerts »." },
      packs: { name: "Vendez des packs", desc: "Des lots de produits vendus ensemble à un seul prix." },
      cash_drawer: { name: "Comptez le tiroir-caisse", desc: "Ouvrir et fermer le tiroir avec un comptage à l'aveugle chaque jour." },
      customer_menu: { name: "Avez un menu client", desc: "La page que les clients atteignent en scannant votre QR code." },
      finance: { name: "Suivi des bénéfices", desc: "Prix d'achat, dépenses, livraisons et rapports de revenus." },
      suppliers: { name: "Fournisseurs", desc: "Ceux à qui vous achetez, ce que vous leur devez et ce que vous avez payé." },
      mixing: { name: "Mélange", desc: "Matières, recettes et lots pour vos propres e-liquides." },
      savings: { name: "Épargne", desc: "L'argent que vous mettez de côté, hors de la boutique." },
      reminders: { name: "Rappels", desc: "Des notifications à l'heure de votre choix, par exemple votre liste de courses." },
      journal: { name: "Journal", desc: "Qui a fait quoi, quand, et sur quel appareil." },
    },
    advanced: "Tous les réglages",
    needs: "Nécessite : {names}",
    alsoOff: "Désactivé car {names} est désactivé.",
    notInPlan: "Ne fait pas partie de votre offre",
    on: "Activé",
    off: "Désactivé",
    saving: "Enregistrement…",
    saved: "Enregistré.",
    saveFailed: "Impossible d'enregistrer ce changement.",
    notOffered: "Cela ne fait pas partie de votre offre. Demandez une offre plus large pour l'utiliser.",
    alsoTurnsOn: "Cela active aussi {names}.",
    alsoTurnsOff: "Cela désactive aussi {names}.",
    loadFailed: "Impossible de charger vos réglages.",
  },
  ar: {
    title: "ما يستخدمه متجرك",
    intro: "فعّل أو عطّل بحسب طريقة عملك. التعطيل يُخفي فقط: لا شيء يُحذف، ويعود كل شيء عند إعادة التفعيل.",
    howMuch: "ما مقدار ما تحتاجه؟",
    presets: {
      counter: { name: "الصندوق فقط", desc: "البيع وإدارة المنتجات ومتابعة المخزون." },
      profit: { name: "الصندوق والأرباح", desc: "يضيف أسعار الشراء والمصاريف والموردين والتوريدات وما تربحه فعلًا." },
      mixing: { name: "أحضّر سوائلي بنفسي", desc: "يضيف المواد والوصفات والدفعات إلى جانب متابعة الأرباح." },
      full: { name: "كل شيء", desc: "كل ما تشمله باقتك." },
    },
    questions: "وهل أنت…",
    items: {
      credit_sales: { name: "تبيع بالأجل", desc: "يحتفظ الزبائن بدفتر حساب ويسدّدون لاحقًا." },
      preorders: { name: "تستقبل الطلبات المسبقة من القائمة", desc: "يحجز الزبائن المنتجات ويستلمونها عند الصندوق." },
      offers: { name: "تقدّم عروضًا وتخفيضات", desc: "نسبة مئوية، مبلغ ثابت، أو اشترِ X واحصل على Y." },
      packs: { name: "تبيع باقات", desc: "مجموعات منتجات تُباع معًا بسعر واحد." },
      cash_drawer: { name: "تجرد درج النقود", desc: "فتح الدرج وإغلاقه بجرد أعمى كل يوم." },
      customer_menu: { name: "لديك قائمة للزبائن", desc: "الصفحة التي يصل إليها الزبائن بمسح رمز QR الخاص بك." },
      finance: { name: "متابعة الأرباح", desc: "أسعار الشراء والمصاريف والتوريدات وتقارير الإيرادات." },
      suppliers: { name: "الموردون", desc: "من تشتري منهم وما عليك لهم وما دفعته." },
      mixing: { name: "المزج", desc: "المواد والوصفات والدفعات لسوائلك الخاصة." },
      savings: { name: "المدّخرات", desc: "المال الذي تدّخره بعيدًا عن المتجر." },
      reminders: { name: "التذكيرات", desc: "إشعارات في الوقت الذي تختاره، مثل قائمة مشترياتك." },
      journal: { name: "السجل", desc: "من فعل ماذا ومتى وعلى أي جهاز." },
    },
    advanced: "جميع المفاتيح",
    needs: "يتطلب: {names}",
    alsoOff: "معطّل لأن {names} معطّل.",
    notInPlan: "ليس ضمن باقتك",
    on: "مفعّل",
    off: "معطّل",
    saving: "جارٍ الحفظ…",
    saved: "تم الحفظ.",
    saveFailed: "تعذّر حفظ هذا التغيير.",
    notOffered: "هذا ليس ضمن باقتك. اطلب باقة أكبر لاستخدامه.",
    alsoTurnsOn: "هذا يفعّل أيضًا {names}.",
    alsoTurnsOff: "هذا يعطّل أيضًا {names}.",
    loadFailed: "تعذّر تحميل إعداداتك.",
  },
};

export default modules;
